/*
 * Copyright (c) 2026. Bolmate B.V.
 */
import IDate from '@dvrd/idate';
import React, {useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Account from '../../models/accountModel';
import {DASHBOARD_ROUTE} from '../../util/constants';
import {PeriodLabel} from '../../util/enums';
import {sendGaPageView} from '../../util/analytics';
import {onSetCrumbs} from '../../util/events';
import {comparisonLabel, toPeriodRange} from '../../util/overviewUtil';
import {capitalizeFirst} from '../../util/utils';
import {AppContext} from '../misc/appContext';
import DashboardPage from './dashboardPage';
import {PRESET_PERIODS} from '../../util/dateUtil.ts';

// The design opens on "Gisteren": today's numbers are still filling up, so the
// first thing a seller sees would otherwise be a partial day.
const DEFAULT_PRESET = PeriodLabel.YESTERDAY;

function defaultRange(): [IDate, IDate] {
    const yesterday = IDate.now().subtract(1, 'day');
    return [yesterday.clone().startOf('day'), yesterday.clone().endOf('day')];
}

export default function DashboardPageController() {
    const {t} = useTranslation();
    const context = useContext(AppContext);
    const {user} = context.userContext;
    const {accounts} = context.accountContext;
    const [periodRange, setPeriodRange] = useState<[IDate, IDate]>(defaultRange);
    const [periodPreset, setPeriodPreset] = useState<PeriodLabel | null>(DEFAULT_PRESET);
    const [accountIds, setAccountIds] = useState<Array<string>>([]);
    const accountOptions = useMemo(
        () => accounts.map((account: Account) => ({value: account.id, label: account.selectablePlatformLabel})),
        [accounts]
    );
    const allAccountIds = useMemo(() => accounts.map((account: Account) => account.id), [accounts]);
    // The contract takes inclusive calendar dates, so the selector's timestamps
    // are reduced to the days they fall on.
    const period = useMemo(() => toPeriodRange(periodRange[0], periodRange[1]), [periodRange]);
    const periodComparisonLabel = useMemo(() => comparisonLabel(periodPreset, t), [periodPreset, t]);
    const hideComparison = periodPreset === PeriodLabel.ALL;

    function onChangePeriod(range: [IDate, IDate], preset: PeriodLabel | null) {
        setPeriodRange(range);
        setPeriodPreset(preset);
    }

    // The segmented toggle names a preset; its window comes from the same table
    // the dropdown uses, so the two can never drift apart.
    function onChangeQuickPeriod(preset: PeriodLabel) {
        if (preset === PeriodLabel.CUSTOM) return;
        let range = PRESET_PERIODS[preset]();
        if (!range[0] || !range[1]) range = [new IDate('2020-01-01'), IDate.now()];
        onChangePeriod(range as [IDate, IDate], preset);
    }

    function onChangeAccounts(_value: string | null, _selected: boolean, values?: Array<string>) {
        if (values !== undefined) setAccountIds(values);
    }

    // "All stores together" is the default of the design: every account the user
    // can see, resolved once the account list arrives.
    useEffect(() => {
        setAccountIds(current => (current.length ? current.filter(id => allAccountIds.includes(id)) : allAccountIds));
    }, [allAccountIds.join(',')]);

    useEffect(() => {
        sendGaPageView(DASHBOARD_ROUTE);
        onSetCrumbs([{label: t('menu.home', 'Overzicht'), route: DASHBOARD_ROUTE}]);
    }, []);

    return (
        <DashboardPage
            firstName={capitalizeFirst(user?.firstname ?? '')}
            period={period}
            periodRange={periodRange}
            periodPreset={periodPreset}
            comparisonLabel={periodComparisonLabel}
            hideComparison={hideComparison}
            onChangePeriod={onChangePeriod}
            onChangeQuickPeriod={onChangeQuickPeriod}
            accountOptions={accountOptions}
            accountIds={accountIds}
            onChangeAccounts={onChangeAccounts}
        />
    );
}
